import {
    Typography,
    FormControlLabel,
    Switch,
    Grid,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';
import { SettingsFieldCard } from './settings-field-card';
import { CheckOnMeFlowDiagram } from '../../../../../components/check-on-me-flow-diagram';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Every field on this tab is a v2 catalog setting, so the whole tab is
     * read-only. Defaults to disabled so a caller that forgets the prop
     * cannot reopen a legacy write path.
     */
    disabled?: boolean;
}

const notificationOptions = ["NONE", "EXPIRED", "EXPIRED_ESCALATED", "ESCALATED", "ALL"];

export const CheckOnMeSettingsTab: React.FC<Props> = ({ formik, disabled = true }) => {
    const { values, touched, errors, getFieldProps } = formik;

    const handleFieldChange = (field: string, value: any) => {
        formik.setFieldValue(field, value);
    };

    const isCheckOnMeEnabled = values?.checkOnMeConfig === "Enabled";

    return (
        <>
            <Typography fontWeight={500} fontSize={18} mt={2}>
                Check On Me
            </Typography>
            <Typography fontSize={14} mb={2} color="text.secondary">
                Allow users to schedule timed safety check-ins.
            </Typography>

            <CheckOnMeFlowDiagram values={values} />

            {/* Check On Me Enable */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isCheckOnMeEnabled}
                            onChange={(e) => {
                                handleFieldChange('checkOnMeConfig', e.target.checked ? "Enabled" : "Disabled");
                                if (!e.target.checked) {
                                    handleFieldChange('escalateCheckOnMe', false);
                                    handleFieldChange('checkOnMeNotifications', "NONE");
                                    handleFieldChange('checkOnMeRiskReportSurvey', false);
                                    handleFieldChange('checkOnMeDispatchEnabled', false);
                                }
                            }}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Check On Me</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Check On Me feature for this organization. This makes the feature available; control is handled at the Group and Team level.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Check Out Reminder Message */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkoutReminderMessage ?? false}
                            onChange={(e) => handleFieldChange('checkoutReminderMessage', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Check Out Reminder</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Send a reminder message 2 minutes before a Check On Me expires.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Expiration Notification Method */}
            <SettingsFieldCard>
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>Expiration Notification Method</Typography>
                    <Typography color="textSecondary" variant="body2">
                        How to notify the user when their Check On Me expires. "Message" sends a push or SMS; "Call" places an automated phone call.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                    <Select
                        value={values?.expireNotifyMethod || 'MESSAGE'}
                        onChange={(e) => handleFieldChange('expireNotifyMethod', e.target.value)}
                        disabled={disabled || !isCheckOnMeEnabled}
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="MESSAGE">Message</MenuItem>
                        <MenuItem value="CALL">Call</MenuItem>
                    </Select>
                </Grid>
            </SettingsFieldCard>

            {/* Auto Escalate Check On Me */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.escalateCheckOnMe ?? false}
                            onChange={(e) => handleFieldChange('escalateCheckOnMe', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>Auto Escalate Expired Check On Me</Typography>
                    <Typography color="textSecondary" variant="body2">
                        When a Check On Me expires without confirmation, automatically escalate the alert to POM Dispatch after the grace period.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                    <Select
                        {...getFieldProps('escalateCheckOnMeTimeout')}
                        disabled={disabled || !isCheckOnMeEnabled || !values?.escalateCheckOnMe}
                        sx={{ width: 150 }}
                        error={touched.escalateCheckOnMeTimeout && Boolean(errors.escalateCheckOnMeTimeout)}
                        onChange={(e) => handleFieldChange('escalateCheckOnMeTimeout', Number(e.target.value))}
                        value={values?.escalateCheckOnMeTimeout || 1}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(option => (
                            <MenuItem key={option} value={option}>
                                {option} {option === 1 ? 'minute' : 'minutes'}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.escalateCheckOnMeTimeout && errors.escalateCheckOnMeTimeout}
                    </FormHelperText>
                </Grid>
            </SettingsFieldCard>

            {/* Notifications */}
            <SettingsFieldCard>
                <Grid item xs>
                    <Typography fontWeight="600" fontSize={16}>Notifications</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Choose which Check On Me events trigger notifications to Team Leaders. Options: None, Expired, Expired + Escalated, Escalated, All.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                    <Select
                        {...getFieldProps('checkOnMeNotifications')}
                        value={values?.checkOnMeNotifications || 'EXPIRED_ESCALATED'}
                        onChange={(e) => handleFieldChange('checkOnMeNotifications', e.target.value)}
                        disabled={disabled || !isCheckOnMeEnabled || !values?.escalateCheckOnMe}
                        sx={{ width: 200 }}
                        error={touched.checkOnMeNotifications && Boolean(errors.checkOnMeNotifications)}
                    >
                        {notificationOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.checkOnMeNotifications && errors.checkOnMeNotifications}
                    </FormHelperText>
                </Grid>
            </SettingsFieldCard>

            {/* Risk Report Survey */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkOnMeRiskReportSurvey ?? false}
                            onChange={(e) => handleFieldChange('checkOnMeRiskReportSurvey', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Risk Survey</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Prompt users to complete a risk report survey after ending their Check On Me session.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Risk Report Question */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkOnMeRiskReportQuestion ?? false}
                            onChange={(e) => handleFieldChange('checkOnMeRiskReportQuestion', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Risk Question</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Include a risk report question as part of the Check On Me flow.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Risk Report Survey Override */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkOnMeRiskReportSurveyOverride ?? false}
                            onChange={(e) => handleFieldChange('checkOnMeRiskReportSurveyOverride', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Risk Survey Override</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Allow users to skip or override the risk report survey when ending a Check On Me.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Risk Review Score Threshold */}
            <SettingsFieldCard>
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>Risk Review Score Threshold</Typography>
                    <Typography color="textSecondary" variant="body2">
                        The minimum risk score (1-5) that triggers a review flag. Lower values flag more visits for review. Requires Risk Report Survey to be enabled.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                    <Select
                        value={values?.riskReviewScoreThreshold ? Number(values.riskReviewScoreThreshold) : ''}
                        onChange={(e) => handleFieldChange('riskReviewScoreThreshold', Number(e.target.value))}
                        disabled={disabled || !isCheckOnMeEnabled || !values?.checkOnMeRiskReportSurvey}
                        sx={{ width: 120 }}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>Select</MenuItem>
                        {[1, 2, 3, 4, 5].map(option => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.riskReviewScoreThreshold && errors.riskReviewScoreThreshold}
                    </FormHelperText>
                </Grid>
            </SettingsFieldCard>

            {/* Emergency Contacts */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkOnMeEmergencyContacts ?? false}
                            onChange={(e) => handleFieldChange('checkOnMeEmergencyContacts', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Emergency Contacts</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the emergency contacts feature for Check On Me alerts, allowing users to designate contacts who receive notifications.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Dispatch Integration */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.checkOnMeDispatchEnabled ?? false}
                            onChange={(e) => handleFieldChange('checkOnMeDispatchEnabled', e.target.checked)}
                            disabled={disabled || !isCheckOnMeEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Dispatch Integration</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable POM Dispatch integration for Check On Me alerts. When enabled, expired and escalated alerts are sent to the dispatch team.
                    </Typography>
                </Grid>
            </SettingsFieldCard>
        </>
    );
};
